'use client';
import { getFormattedCurrency, getFormattedDateAndTime } from '@svenskaspelab/poolbetting/core/lib/client';
import type { DateTime } from '@svenskaspelab/poolbetting/schema/frameworks';
import PowerplayLogo from '@svenskaspelab/ui/react/brands/PowerplayIcon';
import type { ReactNode } from 'react';

type CouponExternalOverviewProps = {
  uploadedAt: DateTime;
  clientName: string;
  regCloseTime: DateTime;
  numberOfRows?: number;
  systemTypeLabel: string;
};

/**
 * CouponExternalOverview component renders an overview of the external coupon, showing product information and a button to show balance.
 *
 * @param props - The props object.
 * @param props.uploadedAt - The date and time when the coupon was uploaded.
 * @param props.clientName - The name of the client who uploaded the coupon.
 * @param props.regCloseTime - The close time for placing bets.
 * @param props.numberOfRows - The number of rows/systems in the coupon.
 * @param props.systemTypeLabel - The label for the system type used in the coupon.
 * @returns A React component displaying an overview of the external coupon.
 */
export default function CouponExternalOverview({
  uploadedAt,
  clientName,
  regCloseTime,
  numberOfRows,
  systemTypeLabel,
}: CouponExternalOverviewProps): ReactNode {
  return (
    <div className="flex w-full flex-col items-center px-6 py-8 text-center">
      <div className="flex w-full max-w-65 flex-col items-center gap-y-6">
        <div className="flex flex-col items-center gap-y-2">
          <div className="text-xs text-neutral-600">Produkt</div>
          <div className="flex h-12 w-12 items-center justify-center rounded text-xl font-bold text-white">
            <PowerplayLogo width={64} height={64} />
          </div>
        </div>

        <div className="flex w-full flex-col gap-y-4">
          <div>
            <div className="text-xs text-neutral-600">Uppladdad</div>
            <div className="text-sm font-semibold">
              {getFormattedDateAndTime(uploadedAt, { timeFormatOptions: 'hourMinute', formatOptions: 'timeOnly' })}
            </div>
          </div>

          <div>
            <div className="text-xs text-neutral-600">Klient</div>
            <div className="text-sm font-semibold">{clientName}</div>
          </div>

          <div>
            <div className="text-xs text-neutral-600">Spelstopp</div>
            <div className="text-sm font-semibold">
              {getFormattedDateAndTime(regCloseTime, {
                timeFormatOptions: 'hourMinute',
                formatOptions: 'dateAndTime',
                dateFormatOptions: 'dayAsText',
                shouldCapitalizeFirstLetter: true,
                shouldUseNameOfDay: true,
              })}
            </div>
          </div>

          <div>
            <div className="text-xs text-neutral-600">Antal rader</div>
            <div className="text-sm font-semibold">{getFormattedCurrency(numberOfRows ?? 0, { unit: '' })}</div>
          </div>

          <div>
            <div className="text-xs text-neutral-600">Systemtyp</div>
            <div className="text-sm font-semibold">{systemTypeLabel}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
